import Level from './Level';
import Location from '../seek/Location';
import GameCamera from '../seek/GameCamera';
import Seeker from '../seek/Seeker';
import GameHud from '../ui/GameHud';
import OnboardingController from '../controller/OnboardingController';
import { getGA } from 'analytics/GoogleAnalytics';

export default class LevelSeek extends Level {
  /* eslint-disable max-statements */
  create() {
    this.container = this.game.add.group();

    //
    this.location = new Location(this.game, this.locationData, this.levelData.sceneWidth);
    this.location.onFindChar.add(this.handleFindChar, this);
    this.container.addChild(this.location);

    //
    this.seeker = new Seeker(
      this.game,
      this.seekerId,
      this.locationData.nav_points,
      this.levelData.start_point,
      100,
      this.levelData.sceneWidth - 100
    );
    this.seeker.onStartWalk.add(this.handleSeekerStartWalk, this);
    this.seeker.onPosition.add(this.handleSeekerOnPosition, this);
    this.location.placeSeeker(this.seeker);

    // place characters
    this.charsData = this.levelData.characters.filter((e) => e.id !== this.seekerId);
    this.location.placeCharacters(this.charsData, this.levelData.slots, this.levelData.difficulty);

    // add GameCamera
    this.camera = new GameCamera(this.game, this.location);
    this.camera.setPosition(this.seeker.x);
    this.camera.setTarget(this.seeker);

    // keyboard control
    this.game.input.keyboard.removeKeyCapture(Phaser.Keyboard.LEFT);
    this.game.input.keyboard.removeKeyCapture(Phaser.Keyboard.RIGHT);

    //
    this.handleResize();

    //
    this.gameHud = new GameHud(this.game, this.charsData);
    this.container.addChild(this.gameHud);
    this.gameHud.onScrollLeft.add(this.handleScrollLeft, this);
    this.gameHud.onScrollRight.add(this.handleScrollRight, this);
    if (this.location.sceneWidth > 2000) {
      this.gameHud.showScrollArrows(!this.seeker.atFirstNavPoint(), !this.seeker.atLastNavPoint());
    }

    //
    this.toFind = this.charsData.length;
    this.found = 0;
    this.gameStarted = false;
    this.timeToWiggle = 5;

    // hint captions
    this.cptHint = this.game.cache.getJSON('vo_ui_captions');

    // show count overlay
    this.gameHud.onCountEnd.addOnce(this.handleCountEnd, this);
    this.gameHud.showOverlayCount(this.seekerId, false);
    // this.handleCountEnd(); // debug

    // transition
    this.transition = this.game.controller.getTransition();
    this.transition.fadeOut();
  }
  /* eslint-enable */

  handleCountEnd() {
    this.transition.onFadeEnd.addOnce(this.handleTransitionEndToStart, this);
    this.transition.fadeIn();
  }

  handleTransitionEndToStart() {
    this.gameHud.removeOverlayCount();
    this.transition.fadeOut();

    this.gameStarted = true;

    // enable controls
    this.location.enableControls();

    // onboarding
    if (this.levelData.onboarding) {
      this.game.onboarding = new OnboardingController(
        this.game,
        this.levelData.onboarding,
        this.game.cache.getJSON(this.levelData.onboarding + '_config'),
        this
      );
      this.game.onboarding.onEnd.addOnce(this.handleOnboardingEnd, this);
      this.game.onboarding.start();
    }
  }

  //
  handleOnboardingEnd() {
    // init captions once again
    this.game.onboarding.destroy();
    this.game.onboarding = null;
    this.game.springRollCaptionsPlugin.setData(this.cptData);
  }

  //
  handleResize() {
    this.calculateNavPoints();
    this.seeker.navPoints = this.navPoints;
    this.setCurrentNavPoint();
    this.seeker.currentNavId = this.navPointId;
  }

  handleScrollLeft() {
    this.setCurrentNavPoint();
    this.seeker.currentNavId = this.navPointId;

    this.seeker.gotoPrevNavPoint();
    this.gameHud.hideScrollArrows();
  }

  handleScrollRight() {
    this.setCurrentNavPoint();
    this.seeker.currentNavId = this.navPointId;
    this.seeker.gotoNextNavPoint();

    this.gameHud.hideScrollArrows();
  }

  handleSeekerOnPosition() {
    this.setCurrentNavPoint();
    this.seeker.currentNavId = this.navPointId;

    if (this.location.sceneWidth > 2000) {
      this.gameHud.showScrollArrows(!this.seeker.atFirstNavPoint(), !this.seeker.atLastNavPoint());
    }
  }

  handleSeekerStartWalk() {
    this.gameHud.hideScrollArrows();
  }

  handleFindChar(characterId) {
    // set captions
    if (this.game.onboarding) {
      this.game.springRollCaptionsPlugin.setData(this.cptData);
    }
    // make seeker happy for short time

    // show in hud found character
    this.gameHud.charactersList.revealCharacter(characterId);

    // play vo "found you" with small delay for stars sfx
    const ind = Math.floor(3 * Math.random()); // todo: add voiceover 017
    const ids = ['011', '015', '016'];
    const textId = 'vo_01_' + this.seekerId + '_' + ids[ind];
    this.game.springRollCaptionsPlugin.playCaption(textId);
    this.game.sounds.playVO(textId, {
      spriteID: this.seekerId + '_vo',
      delay: 450,
    });

    // set talking animation
    // const snd = this.game.sounds.getSfx(textId, this.seekerId + '_vo');
    // console.log(snd);
    // console.log('duration', snd.duration);
    const data = this.cptData[textId];
    const time = data[data.length - 1].end;
    this.seeker.stop();
    this.seeker.pointCharacter();
    this.seeker.talk(time, 450);

    // register next seeker -> first found character
    if (this.found === 0) {
      this.game.controller.nextSeekerId = characterId;
    }

    //
    this.found++;
    if (this.found >= this.toFind) {
      // all found - end of the level
      this.seeker.allFound = true;
      //
      this.game.time.events.add(time + 450, this.showResult, this);
    } else {
      // seek next
      this.game.time.events.add(time + 450, this.enableControls, this);
    }

    //
    this.gameHud.arrowsContainer.visible = false;
    this.location.disableControls();
    this.location.lockCharacters();
    this.gameHud.charactersList.lock();
  }

  showResult() {
    this.transition.onFadeEnd.addOnce(this.handleTransitionEndToResult, this);
    this.transition.fadeIn();
  }

  enableControls() {
    this.gameHud.arrowsContainer.visible = true;
    this.location.enableControls();
    this.location.unlockCharacters();
    this.gameHud.charactersList.unlock();
  }

  enableKeyboardControls() {}

  handleTransitionEndToResult() {
    this.gameHud.onCloseResult.addOnce(this.handleCloseResult, this);
    this.gameHud.showOverlayResult(this.seekerId, this.charsData);
    this.transition.fadeOut();

    // tracking
    let season = this.game.controller.location;
    season = season.replace(/^\w/, (chr) => chr.toUpperCase());
    const action = 'EndLevel_' + season + '_Level_' + (this.levelData.id + 1);
    getGA().track({ action: action });
  }

  handleCloseResult() {
    this.transition.onFadeEnd.addOnce(this.handleTransitionEndToNextLevel, this);
    this.transition.fadeIn();
  }

  handleTransitionEndToNextLevel() {
    this.game.controller.endLevel();
  }

  //
  showClue(characterId) {
    if (this.clueId !== characterId) {
      // simple VO
      this.clueId = characterId;
      this.clueCount = 1;

      let vo = 'ui_01_elinor_101';
      if (characterId === 'twins') vo += '_twins';
      else if (Math.random() < 1) {
        // choose alternative version
        if (characterId === 'elinor' || characterId === 'olive') vo += '_her';
        else vo += '_him';
      }
      const voIds = ['ui_01_elinor_' + characterId, vo];
      this.playHintVOs(voIds);
      this.hintPlaying = true;

      this.seeker.stopTalking();
    } else if (!this.hintPlaying) {
      // pan camera to hidden character
      this.seeker.stopTalking();
      this.gameHud.charactersList.lock();

      this.location.disableControls();
      this.location.lockCharacters();
      this.gameHud.arrowsContainer.visible = false;

      const target = this.location.findSlotWithCharacter(characterId);
      this.camera.setTarget(target);
      this.camera.onPosition.addOnce(this.handleCameraOnClueTarget, this);
    }
  }

  handleCameraOnClueTarget() {
    const voIds = ['ui_01_elinor_102', 'ui_01_elinor_' + this.clueId];
    this.playHintVOs(voIds);

    //
    if (this.clueCount === 2) {
      const target = this.location.findSlotWithCharacter(this.clueId);
      target.showBlings();
    }
    this.clueCount = 2;

    //
    this.game.time.events.add(2500, this.returnToSeeker, this);
  }

  returnToSeeker() {
    this.camera.setTarget(this.seeker);
    this.camera.onPosition.addOnce(this.handleCameraOnSeeker, this);
  }

  handleCameraOnSeeker() {
    // unlock everything

    this.location.enableControls();
    this.location.unlockCharacters();
    this.gameHud.arrowsContainer.visible = true;

    this.gameHud.charactersList.unlock();
  }

  playHintVOs(voIds) {
    this.stopHintVO();

    this.game.springRollCaptionsPlugin.setData(this.cptHint);
    this.voIds = voIds;
    this.voInd = 0;
    this.playHintVO(this.voIds[this.voInd]);
  }

  playHintVO(voId) {
    this.audio = this.game.sounds.playVO(voId, {
      spriteID: 'vo_ui',
      onComplete: this.handleHintVOEnd,
      onCompleteScope: this,
    });
    this.game.springRollCaptionsPlugin.playCaption(voId);
  }

  handleHintVOEnd() {
    this.audio = null;

    this.voInd++;
    if (this.voInd < this.voIds.length) {
      this.playHintVO(this.voIds[this.voInd]);
    } else {
      this.game.springRollCaptionsPlugin.setData(this.cptData);
      this.hintPlaying = false;
    }
  }

  stopHintVO() {
    if (this.audio) {
      this.voInd = 999;
      this.audio.stop();
      this.audio = null;
      this.game.springRollCaptionsPlugin.stopCaption();
    }
  }

  //
  update() {
    const dt = Math.min(0.1, this.game.time.elapsedMS / 1000);

    //
    this.timeToWiggle -= dt;

    // keyboard controls
    if (this.location.controlsEnabled) {
      if (this.game.input.keyboard.isDown(Phaser.Keyboard.LEFT)) {
        this.seeker.gotoPosition(this.seeker.x - 100);
      }
      if (this.game.input.keyboard.isDown(Phaser.Keyboard.RIGHT)) {
        this.seeker.gotoPosition(this.seeker.x + 100);
      }
    }

    //
    this.seeker.step(dt);
    this.location.step(dt);
    this.camera.step(dt);

    // update onboarding controller
    if (this.game.onboarding) {
      this.game.onboarding.step(dt);
    }
  }

  shutdown() {
    //
    if (this.game.onboarding) {
      this.game.onboarding.onEnd.remove(this.handleOnboardingEnd, this);
      this.game.onboarding.destroy();
      this.game.onboarding = null;
    }

    if (this.seeker) {
      this.seeker.destroy();
      this.seeker = null;
    }

    // clear up
    this.container.destroy(true);
    this.container = null;

    super.shutdown();
  }
}
